import { createMCPClient, MCPClient } from "@ai-sdk/mcp";
import { useEffect, useMemo, useState } from "react";
import { useShallow } from "zustand/react/shallow";

import { Edit, Magnify } from "components/icons/generated";
import { TOOL_CALL_DESCRIPTION_FIELD } from "components/AiChat/constants";
import { IconComponent } from "types/Icon";

import { ChatApprovalMode, ChatMode } from "../types";
import { McpToolSet } from "../../types";
import useChatStore from "../store/useStore";

let mcpClientPromise: Promise<MCPClient> | undefined;

type McpToolsState = {
  tools: McpToolSet;
  readOnlyToolNames: Set<string>;
  toolIcons: Record<string, IconComponent>;
};

let cachedState: McpToolsState | undefined;

const EMPTY_TOOLS: McpToolSet = {};

const TOOL_CALL_DESCRIPTION_SCHEMA = {
  type: "string",
  description:
    "A short, user-friendly description of the action. Examples: 'Listing all projects', 'Creating a new policy', 'Deleting the resource', 'Updating project settings'",
};

const CONVERSATION_ID_HEADER = "X-Spacelift-Conversation-Id";

const getIntentMcpClient = async () => {
  if (!mcpClientPromise) {
    mcpClientPromise = createMCPClient({
      transport: {
        type: "http",
        url: new URL("/mcp", window.location.origin).toString(),
        // the transport calls fetch as a method, which breaks Bugsnag's wrapper
        fetch: (input, init) => {
          // the client is shared across conversations, so the id is read per
          // request rather than baked into the transport headers
          const headers = new Headers(init?.headers);
          const { chat } = useChatStore.getState();

          if (chat) {
            headers.set(CONVERSATION_ID_HEADER, chat.id);
          }

          return globalThis.fetch(input, { ...init, headers } as RequestInit);
        },
      },
    }).catch((error) => {
      mcpClientPromise = undefined;
      throw error;
    });
  }
  return mcpClientPromise;
};

const useIntentMcpClient = () => {
  const { mode, approvalMode, setMcpFilteredTools } = useChatStore(
    useShallow((state) => ({
      mode: state.mode,
      approvalMode: state.approvalMode,
      setMcpFilteredTools: state.setMcpFilteredTools,
    }))
  );

  const [mcpState, setMcpState] = useState<McpToolsState | undefined>(cachedState);
  const [isLoading, setIsLoading] = useState(!cachedState);
  const [error, setError] = useState<Error | undefined>(undefined);

  useEffect(() => {
    if (cachedState) {
      return;
    }

    let cancelled = false;

    const init = async () => {
      try {
        const client = await getIntentMcpClient();

        const { tools: definitions } = await client.listTools();

        if (definitions.length === 0) {
          throw new Error("MCP returned no tools");
        }

        const augmentedDefinitions = definitions.map((definition) => ({
          ...definition,
          inputSchema: {
            ...definition.inputSchema,
            properties: {
              ...definition.inputSchema.properties,
              [TOOL_CALL_DESCRIPTION_FIELD]: TOOL_CALL_DESCRIPTION_SCHEMA,
            },
            required: [
              ...(Array.isArray(definition.inputSchema.required)
                ? definition.inputSchema.required
                : []),
              TOOL_CALL_DESCRIPTION_FIELD,
            ],
          },
        }));

        const toolIcons: Record<string, IconComponent> = {};

        for (const definition of definitions) {
          toolIcons[definition.name] = definition.annotations?.readOnlyHint ? Magnify : Edit;
        }

        const state: McpToolsState = {
          tools: client.toolsFromDefinitions({ tools: augmentedDefinitions }),
          readOnlyToolNames: new Set(
            augmentedDefinitions.filter((d) => d.annotations?.readOnlyHint).map((d) => d.name)
          ),
          toolIcons,
        };

        if (!cancelled) {
          cachedState = state;
          setMcpState(state);
          setIsLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error("Failed to connect to MCP"));
          setIsLoading(false);
        }
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, []);

  const filteredTools = useMemo(() => {
    if (!mcpState) {
      return EMPTY_TOOLS;
    }

    const gateWriteTools = approvalMode === ChatApprovalMode.MANUAL;

    const tools: McpToolSet = {};

    for (const [name, tool] of Object.entries(mcpState.tools)) {
      const isReadOnly = mcpState.readOnlyToolNames.has(name);

      if (mode !== ChatMode.BUILD && !isReadOnly) {
        continue;
      }

      tools[name] = gateWriteTools && !isReadOnly ? { ...tool, needsApproval: true } : tool;
    }

    return tools;
  }, [mcpState, mode, approvalMode]);

  useEffect(() => {
    setMcpFilteredTools(filteredTools);
  }, [filteredTools, setMcpFilteredTools]);

  return {
    toolIcons: mcpState?.toolIcons ?? {},
    isLoading,
    error,
  };
};

export default useIntentMcpClient;
